import {
  calculateProjectDuration,
  getParticleEngine,
  healCaptionPlacement,
  healCaptionTrackOrder,
  healLegacyCaptionScale,
  normalizeGeneratedShaders,
  normalizeProjectMotionFields,
  type Project,
} from "@openreel/core";
import { useEngineStore } from "../engine-store";

/**
 * Brings a project from storage up to what the running code expects.
 *
 * Opening from Recent and recovering an auto-save used to run their own
 * hand-copied versions of this, and they had already drifted: recovery never
 * ran the motion normalisation or the duration repair, so a project reopened
 * the ordinary way came back subtly different from the same project opened
 * from the project list.
 */
export function prepareOpenedProject(incoming: Project): Project {
  const motionNormalized = normalizeProjectMotionFields(incoming);

  const healed: Project = {
    ...motionNormalized,
    generatedShaders: normalizeGeneratedShaders(
      motionNormalized.generatedShaders,
    ),
    // Captions saved before the short-side scale fix carry the broken size in
    // their own style; healing at load is what lets an affected project simply
    // open correctly instead of asking the user to restyle blind, invisible
    // text.
    // A caption dragged against the old mismatched selection box could walk
    // its centre off the frame and stick there, rendering off screen; healing
    // both the scale and the placement is what puts such a caption back on
    // screen when the project reopens.
    textClips: healCaptionPlacement(
      healLegacyCaptionScale(
        motionNormalized.textClips ?? [],
        motionNormalized.settings.width,
        motionNormalized.settings.height,
      ),
    ),
    // Projects saved while the Captions track was appended have it at the back
    // of the paint order, behind the footage; same reasoning.
    timeline: {
      ...motionNormalized.timeline,
      tracks: healCaptionTrackOrder(motionNormalized.timeline.tracks),
    },
  };

  const computedDuration = calculateProjectDuration(healed);
  if (computedDuration === healed.timeline.duration) {
    return healed;
  }
  // Older saves never persisted timeline.duration at all.
  return {
    ...healed,
    timeline: { ...healed.timeline, duration: computedDuration },
  };
}

/**
 * Hands the opened project to the engines that hold their own copy of it.
 *
 * Every load is unconditional. The engines are process-wide singletons that
 * the preview and the exporter read directly, so skipping a load because the
 * incoming project has nothing of that kind leaves the previous project's
 * text, shapes or particles rendering on top of the new one wherever a clip id
 * collides. Recovery used to guard each of these on the incoming project
 * having the field.
 */
export function loadProjectIntoEngines(project: Project): void {
  const titleEngine = useEngineStore.getState().getTitleEngine();
  const graphicsEngine = useEngineStore.getState().getGraphicsEngine();

  titleEngine?.loadTextClips(project.textClips ?? []);
  if (graphicsEngine) {
    graphicsEngine.loadShapeClips(project.shapeClips ?? []);
    graphicsEngine.loadSVGClips(project.svgClips ?? []);
    graphicsEngine.loadStickerClips(project.stickerClips ?? []);
  }
  getParticleEngine().loadEffects(project.particleEffects ?? []);
}
